/**
 * Dismissing a clarifying-question round (contract C7):
 * POST /chat/threads/{thread_id}/clarify/{round_id}/dismiss with an
 * Idempotency-Key → 204; 404 (unknown round, not this caller's thread); 409
 * `clarify_round_closed`. A replay with the same key returns the cached 204.
 *
 * The card has already closed when this runs (spec "Failure handling": the
 * dismiss call failing still closes the card locally). One silent retry, with
 * the SAME key, so a first attempt that did land is replayed rather than
 * repeated. A guest dismisses with the guest token (N6).
 */
import { ApiError, apiFetch, generateUuidV7 } from "@/lib/api-client";

/** dismissed: the server closed it · closed: it was not open any more · failed: unknown. */
export type ClarifyDismissOutcome = "dismissed" | "closed" | "failed";

export function clarifyDismissPath(threadId: string, roundId: string): string {
  return `/chat/threads/${encodeURIComponent(threadId)}/clarify/${encodeURIComponent(roundId)}/dismiss`;
}

export async function dismissClarifyRound({
  threadId,
  roundId,
  guest,
}: {
  threadId: string;
  roundId: string;
  guest: boolean;
}): Promise<ClarifyDismissOutcome> {
  const path = clarifyDismissPath(threadId, roundId);
  const idempotencyKey = generateUuidV7();
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await apiFetch<undefined>({
        method: "POST",
        path,
        idempotencyKey,
        ...(guest ? { allowGuest: true } : {}),
      });
      return "dismissed";
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 409)) {
        return "closed";
      }
    }
  }
  return "failed";
}
